import React, { useEffect, useRef } from "react";
import {
  CatalogNavigationContainer,
  NavigationDropdownContainer
} from "./CatalogNavigation";
import {
  DesktopHamburgerDropdown,
  MobileHamburgerDrawer
} from "./HamburgerDropdown";
import UtilityBar from "./UtilityBar/UtilityBar";
import { cn } from "utils/cn";
import useIsHomePage from "@RHCommerceDev/hooks/useIsHomePage";
import { processEnvServer } from "@RHCommerceDev/hooks/useSsrHooks";
import { Theme, useMediaQuery } from "@mui/material";
import { getReqContext } from "@RHCommerceDev/utils/reqContext";
import { getSelectorsByUserAgent } from "react-device-detect";
import { useSetHeaderRect } from "@RHCommerceDev/hooks/useSetHeaderRect";
import { useHeaderAtom } from "@RHCommerceDev/hooks/atoms/useHeaderAtom";

const Header = () => {
  // On SSR, detect if device is mobile from user-agent and set `mobile` flag.
  const req = getReqContext();
  let mobile = false;
  const userAgentSSR = req && req?.headers["user-agent"];
  if (userAgentSSR) {
    const { isMobile } = getSelectorsByUserAgent(userAgentSSR);
    mobile = !!isMobile;
  }

  //On CSR, Check if viewport is ≥ "md"
  const mdUp = useMediaQuery<Theme>(theme => theme.breakpoints.up("md"), {
    noSsr: !processEnvServer
  });

  const isLargeDevice = processEnvServer ? !mobile : mdUp;

  const isHomePage = useIsHomePage();

  const { state, closeCategoryDropdown } = useHeaderAtom();

  const isHamburgerDropdownOpen = state.isHamburgerDropdownOpen;
  const isCategoryDropdownOpen = state.selectedCategoryIndex !== -1;

  const isDropdownOpen = isCategoryDropdownOpen || isHamburgerDropdownOpen;

  const headerRef = useRef<HTMLDivElement>(null);
  useSetHeaderRect(headerRef);

  const [scrollbarWidth, setScrollbarWidth] = React.useState(0);

  useEffect(() => {
    const bodyEl = document.body;
    const update = () => {
      if (isCategoryDropdownOpen || isHamburgerDropdownOpen || !bodyEl) return;
      setScrollbarWidth(
        (window.innerWidth || bodyEl.offsetWidth) - bodyEl.clientWidth
      );
    };
    update();

    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("resize", update);
    };
  }, [isCategoryDropdownOpen, isHamburgerDropdownOpen]);

  return (
    <>
      <header
        ref={headerRef}
        style={{
          paddingRight: isDropdownOpen ? `${scrollbarWidth}px` : undefined
        }}
        className={cn(
          "absolute top-0 z-[1100] flex w-full flex-col",
          "h-[82px] sm:h-[102px] md:h-[120px]",
          {
            "bg-rh-buff text-rh-gallery-text": true,
            "bg-transparent text-white": isDropdownOpen && isLargeDevice,
            "bg-transparent bg-[linear-gradient(to_bottom,rgba(112,86,64,0.5)_0%,rgba(112,86,64,0)_100%)] text-white":
              isHomePage && !(isDropdownOpen && isLargeDevice)
          }
        )}
        onClick={() => {
          /* This is used to apply click away effect on category dropdown */
          if (isCategoryDropdownOpen) {
            closeCategoryDropdown();
          }
        }}
      >
        {<UtilityBar />}

        {!isHamburgerDropdownOpen && <CatalogNavigationContainer />}
      </header>

      {/* Category Dropdown */}
      {isLargeDevice && <NavigationDropdownContainer />}

      {/* Hamburger Dropdown for Desktop and Drawer for Mobile*/}
      {isLargeDevice ? <DesktopHamburgerDropdown /> : <MobileHamburgerDrawer />}
    </>
  );
};

export default Header;
